import React, { useEffect } from 'react';
import Modal from 'react-modal';
import Offers from './screens/Offers';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider, useDispatch, useSelector } from 'react-redux';
import {
  store,
  storeContentfulData,
  useContentfulData,
  environment,
  RootState,
} from '@rb-serve-app/core';
import Home from './screens/Home';
import Layout from './ui/Layout/Layout';
import './components/pdfWorkerSetup';
import Statements from './screens/Statements';
import ManageCard from './screens/ManageCard';
import AccountDetailsPage from './screens/AccountDetailsPage';
import LockUnlockCard from './components/LockUnlockCard';
import EqualPaymentPlans from './screens/EqualPaymentPlan';
import SelectEqualPaymentPlan from './screens/SelectEqualPaymentPlan';
import ReviewEppPlan from './screens/ReviewEppPlan';
import Transactions from './screens/Transactions';
import SignIn from './screens/SignIn';
import ProtectedRoute from './ui/Routes/ProtectedRoute';
import PublicRoute from './ui/Routes/PublicRoute';
import ManageAlerts from './screens/ManageAlerts';
import ManagePasskey from './screens/ManagePasskey';
import Rewards from './components/Rewards';
import ContactUs from './screens/ContactUs';
import CardFeatures from './screens/CardFeatures';
import PreAuthorizedDebit from './screens/PreAuthorizedDebit';
import MyProfile from './components/MyProfile';
import ViewServiceRequest from './components/ViewServiceRequest';
import RequestSupplementaryCard from './components/RequestSupplementaryCard';
import AddCardHolderForm from './components/AddCardHolderForm';
import SecureMessage from './screens/SecureMessages';
import CashbackRewards from './screens/CashbackRewards';
import ViewRewards from './components/ViewRewards';
import RoamLikeHomeEligible from './screens/ManageCard/RoamLikeHomeEligible';
import SiteDownComponent from './screens/SiteDownComponent';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import LinkAccount from './screens/LinkAccount';
import WelcomeContent from './screens/WelcomeContent';
import WorldLegendCreditRedemption from './screens/WorldLegendCreditRedemption';
import AccountProtection from './screens/AccountProtection';
import OffersMarketingTiles from './screens/Offers/OffermarketingTiles';

Modal.setAppElement('#root');

const queryClient = new QueryClient();
 const lang = localStorage.getItem('language') == 'fr' ? 'fr' : 'en'
function App() {
  const dispatch = useDispatch();

  const { data: contentFulData } = useContentfulData('');
  useEffect(() => {
    if (contentFulData) {
      dispatch(storeContentfulData(contentFulData));
    }
  }, [contentFulData, dispatch]);

  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries, assets } = contentfulData;
  const isSiteDown = entries?.rb_error?.isWebSiteDown || false;

  const RecaptchaMobileExpand = () => {
    useEffect(() => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (!badge) return;
      const toggleExpand = () => {
        // only toggle on touch devices (avoid fighting with hover on desktop)
        if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
          badge.classList.toggle('expanded');
        }
      };
      const closeOnOutsideClick = (e: Event) => {
        if (!badge.contains(e.target as Node)) {
          badge.classList.remove('expanded');
        }
      };
      badge.addEventListener('click', toggleExpand);
      document.addEventListener('click', closeOnOutsideClick);
      return () => {
        badge.removeEventListener('click', toggleExpand);
        document.removeEventListener('click', closeOnOutsideClick);
      };
    }, []);
    return null;
  };

  return (
    // <Provider store={store}>
    //   <QueryClientProvider client={queryClient}>
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/sign-in"
            element={
              <PublicRoute>
                <Layout>
                  {isSiteDown ? (
                    <SiteDownComponent />
                  ) : (
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <RecaptchaMobileExpand />
                      <SignIn />
                    </GoogleReCaptchaProvider>
                  )}
                </Layout>
              </PublicRoute>
            }
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <Home />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/welcome-content"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <WelcomeContent />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/offers"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <OffersMarketingTiles />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/redeem-rewards"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <CashbackRewards />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/link-account"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <LinkAccount />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <MyProfile />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/roam-like-home"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <RoamLikeHomeEligible />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/service-request"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <ViewServiceRequest />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/statements"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <Statements />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />

          <Route
            path="/transactions"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <Transactions />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />

          <Route
            path="/account-details"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <AccountDetailsPage />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/account-protection"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <AccountProtection />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/manage-card"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <ManageCard />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/rewards"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <Rewards />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/view-rewards"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <ViewRewards />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/world-legend-credit-redemption"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <WorldLegendCreditRedemption />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/lock-unlock"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <LockUnlockCard />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/equal-payment-plan"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <EqualPaymentPlans />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/select-equal-payment-plan"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <SelectEqualPaymentPlan />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/review-epp-plan"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <ReviewEppPlan />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/manage-alerts"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <ManageAlerts />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/manage-passkey"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <ManagePasskey />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/pre-authorized-debit"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <PreAuthorizedDebit />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/card-features"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <CardFeatures />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/request-supplementary"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <RequestSupplementaryCard />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/supplementary-details"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <GoogleReCaptchaProvider
                      reCaptchaKey={environment.recaptchaSiteKey}
                      language={lang}
                      container={{
                        parameters: {
                          badge: 'bottomright',
                          theme: 'light',
                        },
                      }}
                      scriptProps={{ async: true, defer: true }}
                    >
                      <AddCardHolderForm />
                    </GoogleReCaptchaProvider>
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/secure-messages"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <SecureMessage />
                  </Layout>
                }
              />
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute
                element={
                  <Layout>
                    <Home />
                  </Layout>
                }
              />
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
    //   </QueryClientProvider>
    // </Provider>
  );
}

export default App;
